/**
 * StylrManager.ts - Universal style and theme management
 * 
 * CHANGE HISTORY:
 * - 251102_1501 danielbeer: Rebuilt with strict TypeScript from scratch (Excellence Over Speed)
 * - 251102_1246 danielbeer: Enhanced with ImageLibrary and ChangeTracker for universal design editor
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 */
import { nanoid } from 'nanoid';
import { ImageLibrary, getImageLibrary } from './core/ImageLibrary';
import { ChangeTracker, getChangeTracker } from './core/ChangeTracker';
import { getStyleCache, createCSSCacheKey } from './performance/StyleCache';
import { getCSSVariableSetter, getDOMPerformanceMonitor } from './performance/DOMOptimizer';
import { InheritanceManager, getInheritanceManager } from './features/InheritanceManager';
import { VariantManager, getVariantManager } from './features/VariantManager';
import type {
  Style,
  Gallery,
  StylePreference,
  StylrManagerOptions,
  CreateStyleParams,
  UpdateStyleParams,
  StyleVariables
} from './types';

/**
 * StylrManager - Universal style and theme management
 * 
 * Manages styles, galleries, and themes across OpCentral.
 * Integrates with Actr, Interfacr, and Aggregatr orchestrators.
 */
export class StylrManager {
  private actr: any;
  private storage: any;
  private imageLibrary: ImageLibrary;
  private changeTracker: ChangeTracker;
  private styles: Map<string, Style>;
  private galleries: Map<string, Gallery>;
  private activeStyles: Map<string, string>;
  private listeners: Set<(event: string, ...args: any[]) => void>;
  private cache = getStyleCache();
  private cssSetter = getCSSVariableSetter();
  private perfMonitor = getDOMPerformanceMonitor();
  private inheritanceManager: InheritanceManager;
  private variantManager: VariantManager;

  constructor(options: StylrManagerOptions = {}) {
    this.actr = options.actr;
    this.storage = options.storage || options.aggregatr;
    this.styles = new Map();
    this.galleries = new Map();
    this.activeStyles = new Map();
    this.listeners = new Set();
    
    // Initialize ImageLibrary
    this.imageLibrary = getImageLibrary({ actr: this.actr, storage: this.storage });
    
    // Initialize ChangeTracker
    this.changeTracker = getChangeTracker({ actr: this.actr });
    
    // Initialize InheritanceManager and VariantManager
    this.inheritanceManager = getInheritanceManager(this.styles);
    this.variantManager = getVariantManager(this.createStyle.bind(this));
    
    // Load defaults
    this.loadDefaultStyles();
    this.loadDefaultGalleries();
    
    // Load from storage if available
    if (this.storage) {
      this.loadFromStorage();
    }
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }

  // ============================================================================
  // Default Styles & Galleries
  // ============================================================================

  /**
   * Load default built-in styles
   */
  private loadDefaultStyles(): void {
    const defaultStyles: Style[] = [
      // Card styles
      {
        id: 'card-default',
        name: 'Default Card',
        type: 'card',
        gallery: 'cards',
        isDefault: true,
        variables: {
          '--card-bg': '#ffffff',
          '--card-border': '1px solid #e0e0e0',
          '--card-radius': '8px',
          '--card-shadow': '0 2px 4px rgba(0,0,0,0.1)',
          '--card-padding': '16px'
        }
      },
      {
        id: 'card-elevated',
        name: 'Elevated Card',
        type: 'card',
        gallery: 'cards',
        variables: {
          '--card-bg': '#ffffff',
          '--card-border': 'none',
          '--card-radius': '12px',
          '--card-shadow': '0 4px 12px rgba(0,0,0,0.15)',
          '--card-padding': '20px'
        }
      },
      // List styles
      {
        id: 'list-default',
        name: 'Default List',
        type: 'list',
        gallery: 'lists',
        isDefault: true,
        variables: {
          '--list-bg': '#ffffff',
          '--list-border': '1px solid #e0e0e0',
          '--list-item-padding': '12px 16px',
          '--list-item-hover': '#f5f5f5'
        }
      },
      // Button styles
      {
        id: 'button-primary',
        name: 'Primary Button',
        type: 'button',
        gallery: 'buttons',
        isDefault: true,
        variables: {
          '--button-bg': '#007bff',
          '--button-color': '#ffffff',
          '--button-border': 'none',
          '--button-radius': '4px',
          '--button-padding': '8px 16px'
        }
      }
    ];

    defaultStyles.forEach(style => {
      this.styles.set(style.id, style);
    });
  }

  /**
   * Load default galleries
   */
  private loadDefaultGalleries(): void {
    const defaultGalleries: Gallery[] = [
      {
        id: 'cards',
        name: 'Card Styles',
        description: 'Styles for card components',
        styles: ['card-default', 'card-elevated'],
        isDefault: true
      },
      {
        id: 'lists',
        name: 'List Styles',
        description: 'Styles for list components',
        styles: ['list-default'],
        isDefault: true
      },
      {
        id: 'buttons',
        name: 'Button Styles',
        description: 'Styles for button components',
        styles: ['button-primary'],
        isDefault: true
      },
      {
        id: 'custom',
        name: 'Custom Styles',
        description: 'User-created custom styles',
        styles: [],
        isDefault: false
      }
    ];

    defaultGalleries.forEach(gallery => {
      this.galleries.set(gallery.id, gallery);
    });
  }

  // ============================================================================
  // Actr Integration - Action Registration
  // ============================================================================

  /**
   * Register all Stylr actions with Actr
   * Provides CRUD operations, style application, and image library access
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    console.log('[Stylr] Registering actions with Actr');
    
    // Style CRUD actions
    this.actr.register('stylr:create-style', {
      handler: async (params: CreateStyleParams) => {
        console.log('[Stylr] Action: create-style', params);
        return this.createStyle(params);
      }
    });
    
    this.actr.register('stylr:update-style', {
      handler: async (params: UpdateStyleParams) => {
        console.log('[Stylr] Action: update-style', params);
        return this.updateStyle(params.styleId, params.updates);
      }
    });
    
    this.actr.register('stylr:delete-style', {
      handler: async (params: { styleId: string }) => {
        console.log('[Stylr] Action: delete-style', params);
        return this.deleteStyle(params.styleId);
      }
    });
    
    this.actr.register('stylr:get-style', {
      handler: async (params: { styleId: string }) => {
        console.log('[Stylr] Action: get-style', params);
        return this.getStyle(params.styleId);
      }
    });
    
    this.actr.register('stylr:get-styles-by-type', {
      handler: async (params: { type: string }) => {
        console.log('[Stylr] Action: get-styles-by-type', params);
        return this.getStylesByType(params.type);
      }
    });
    
    // Style application actions
    this.actr.register('stylr:apply-style', {
      handler: async (params: { viewType: string; styleId: string }) => {
        console.log('[Stylr] Action: apply-style', params);
        return this.applyStyle(params.viewType, params.styleId);
      }
    });
    
    this.actr.register('stylr:get-active-style', {
      handler: async (params: { type: string }) => {
        console.log('[Stylr] Action: get-active-style', params);
        return this.getActiveStyle(params.type);
      }
    });
    
    this.actr.register('stylr:get-default-style', {
      handler: async (params: { type: string }) => {
        console.log('[Stylr] Action: get-default-style', params);
        return this.getDefaultStyle(params.type);
      }
    });
    
    // Image library actions
    this.actr.register('stylr:upload-image', {
      handler: async (params: any) => {
        console.log('[Stylr] Action: upload-image', params);
        return this.imageLibrary.upload(params);
      }
    });
    
    this.actr.register('stylr:get-image', {
      handler: async (params: { imageId: string }) => {
        console.log('[Stylr] Action: get-image', params);
        return this.imageLibrary.getImage(params.imageId);
      }
    });
    
    this.actr.register('stylr:get-all-images', {
      handler: async () => {
        console.log('[Stylr] Action: get-all-images');
        return this.imageLibrary.getAllImages();
      }
    });
    
    // Change tracking actions
    this.actr.register('stylr:undo', {
      handler: async () => {
        console.log('[Stylr] Action: undo');
        return this.changeTracker.undo();
      }
    });
    
    this.actr.register('stylr:redo', {
      handler: async () => {
        console.log('[Stylr] Action: redo');
        return this.changeTracker.redo();
      }
    });
    
    this.actr.register('stylr:can-undo', {
      handler: async () => {
        return this.changeTracker.canUndo();
      }
    });
    
    this.actr.register('stylr:can-redo', {
      handler: async () => {
        return this.changeTracker.canRedo();
      }
    });
    
    // Style preference CRUD actions
    this.actr.register('stylr:create-preference', {
      handler: async (data: Partial<StylePreference>) => {
        console.log('[Stylr] Action: create-preference', data);
        if (!data.userId || !data.viewType || !data.styleId) {
          throw new Error('Missing required fields: userId, viewType, styleId');
        }
        const pref: StylePreference = {
          id: data.id || nanoid(),
          userId: data.userId,
          viewType: data.viewType,
          styleId: data.styleId,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        this.activeStyles.set(data.viewType, data.styleId);
        return pref;
      }
    });
    
    this.actr.register('stylr:read-preferences', {
      handler: async (_params: { id?: string }) => {
        // Read user's style preferences
        const prefs: Array<{ viewType: string; styleId: string }> = [];
        for (const [viewType, styleId] of this.activeStyles.entries()) {
          prefs.push({ viewType, styleId });
        }
        return prefs;
      }
    });
    
    this.actr.register('stylr:update-preference', {
      handler: async (data: { id: string } & Partial<StylePreference>) => {
        if (data.viewType && data.styleId) {
          this.activeStyles.set(data.viewType, data.styleId);
        }
        return { ...data, updatedAt: Date.now() };
      }
    });
    
    this.actr.register('stylr:delete-preference', {
      handler: async (params: { id: string }) => {
        // Remove style preference
        this.activeStyles.delete(params.id);
        return { id: params.id };
      }
    });
  }

  // ============================================================================
  // Style CRUD Operations
  // ============================================================================

  /**
   * Create a new style
   */
  createStyle(params: CreateStyleParams): Style {
    const style: Style = {
      id: nanoid(),
      name: params.name,
      type: params.type,
      variables: params.variables,
      gallery: params.gallery || 'custom',
      isDefault: params.isDefault || false,
      extends: params.extends, // Support inheritance
      metadata: params.metadata,
      custom: true,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    this.styles.set(style.id, style);
    this.notifyListeners('styleCreated', style);
    
    // Track change
    this.changeTracker.recordChange({
      type: 'create',
      target: {
        type: 'style',
        id: style.id,
        name: style.name
      },
      changes: [{
        property: 'created',
        oldValue: null,
        newValue: style
      }]
    });
    
    return style;
  }

  /**
   * Update an existing style
   */
  updateStyle(styleId: string, updates: Partial<Style>): Style {
    const style = this.styles.get(styleId);
    if (!style) {
      throw new Error(`Style ${styleId} not found`);
    }
    
    // Invalidate cache for this style
    this.cache.invalidateStyle(styleId);
    
    const before = { ...style };
    const updatedStyle: Style = {
      ...style,
      ...updates,
      id: style.id,
      updatedAt: Date.now()
    };
    
    this.styles.set(styleId, updatedStyle);
    this.notifyListeners('styleUpdated', updatedStyle);
    
    // Track change
    this.changeTracker.recordChange({
      type: 'update',
      target: {
        type: 'style',
        id: styleId,
        name: updatedStyle.name
      },
      changes: Object.keys(updates).map(key => ({
        property: key,
        oldValue: (before as any)[key],
        newValue: (updatedStyle as any)[key]
      }))
    });
    
    return updatedStyle;
  }

  /**
   * Delete a style
   */
  deleteStyle(styleId: string): boolean {
    const style = this.styles.get(styleId);
    if (!style) {
      throw new Error(`Style ${styleId} not found`);
    }
    
    if (!style.custom) {
      throw new Error('Cannot delete default styles');
    }
    
    // Invalidate cache for this style
    this.cache.invalidateStyle(styleId);
    
    this.styles.delete(styleId);
    this.notifyListeners('styleDeleted', style);
    
    // Track change
    this.changeTracker.recordChange({
      type: 'delete',
      target: {
        type: 'style',
        id: styleId,
        name: style.name
      },
      changes: [{
        property: 'deleted',
        oldValue: style,
        newValue: null
      }]
    });
    
    return true;
  }

  /**
   * Get a style by ID
   */
  getStyle(styleId: string): Style | undefined {
    return this.styles.get(styleId);
  }

  /**
   * Get all styles (optionally filtered)
   */
  getAllStyles(type: string | null = null, gallery: string | null = null): Style[] {
    let styles = Array.from(this.styles.values());
    
    if (type) {
      styles = styles.filter(s => s.type === type);
    }
    
    if (gallery) {
      styles = styles.filter(s => s.gallery === gallery);
    }
    
    return styles;
  }

  /**
   * Get styles by type
   */
  getStylesByType(type: string): Style[] {
    return Array.from(this.styles.values()).filter(style => style.type === type);
  }

  /**
   * Get active style for a type
   */
  getActiveStyle(type: string): Style | null {
    const activeStyleId = this.activeStyles.get(type);
    if (!activeStyleId) return null;
    return this.styles.get(activeStyleId) || null;
  }

  /**
   * Get default style for a type
   */
  getDefaultStyle(type: string): Style | null {
    const styles = this.getStylesByType(type);
    const defaultStyle = styles.find(style => style.isDefault);
    return defaultStyle || styles[0] || null;
  }

  /**
   * Get resolved style (with inheritance)
   * Cached for performance
   */
  getResolvedStyle(styleId: string): Style | null {
    // Check cache first
    const cached = this.cache.getResolvedStyle(styleId);
    if (cached) return cached;
    
    const style = this.styles.get(styleId);
    if (!style) return null;
    
    // If no inheritance, return as-is
    if (!style.extends) {
      const resolved = { ...style };
      this.cache.setResolvedStyle(styleId, resolved);
      return resolved;
    }
    
    // Resolve parent style
    const parent = this.getResolvedStyle(style.extends);
    if (!parent) {
      const resolved = { ...style };
      this.cache.setResolvedStyle(styleId, resolved);
      return resolved;
    }
    
    // Merge variables (child overrides parent)
    const resolved = {
      ...style,
      variables: {
        ...parent.variables,
        ...style.variables
      }
    };
    
    // Cache the resolved style
    this.cache.setResolvedStyle(styleId, resolved);
    return resolved;
  }

  // ============================================================================
  // Style Application
  // ============================================================================

  /**
   * Set active style for a view type
   */
  setActiveStyle(viewType: string, styleId: string): boolean {
    const style = this.styles.get(styleId);
    if (!style) {
      throw new Error(`Style ${styleId} not found`);
    }
    
    this.activeStyles.set(viewType, styleId);
    this.notifyListeners('activeStyleChanged', { viewType, styleId });
    
    // Apply to DOM
    this.applyStyle(viewType, styleId);
    
    return true;
  }

  /**
   * Apply style to DOM (generate CSS variables)
   * Optimized with batched DOM updates
   */
  applyStyle(viewType: string, styleId: string): void {
    this.perfMonitor.measure('applyStyle', () => {
      const style = this.getResolvedStyle(styleId);
      if (!style) return;
      
      // Batch CSS variable updates
      const vars: Record<string, string> = {};
      
      // Add prefixed variables
      Object.entries(style.variables).forEach(([key, value]) => {
        const prefixedKey = `${key}-${viewType}`;
        vars[prefixedKey] = String(value);
      });
      
      // Add unprefixed variables
      Object.entries(style.variables).forEach(([key, value]) => {
        vars[key] = String(value);
      });
      
      // Apply all at once (batched)
      this.cssSetter.setMultiple(vars);
    });
  }

  /**
   * Generate CSS from style
   * Cached for performance
   */
  generateCSS(styleId: string, selector = '.styled-element'): string {
    // Check cache first
    const cacheKey = createCSSCacheKey(styleId, selector);
    const cached = this.cache.getCSS(cacheKey);
    if (cached) return cached;
    
    // Generate CSS
    const style = this.getResolvedStyle(styleId);
    if (!style) return '';
    
    const rules = Object.entries(style.variables)
      .map(([key, value]) => `  ${key}: ${value};`)
      .join('\n');
    
    const css = `${selector} {\n${rules}\n}`;
    
    // Cache it
    this.cache.setCSS(cacheKey, css);
    return css;
  }

  // ============================================================================
  // Gallery Operations
  // ============================================================================

  /**
   * Create a gallery
   */
  createGallery(data: Partial<Gallery>): Gallery {
    const gallery: Gallery = {
      id: data.id || nanoid(),
      name: data.name || 'New Gallery',
      description: data.description || '',
      styles: data.styles || [],
      isDefault: data.isDefault || false,
      metadata: data.metadata || {},
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    this.galleries.set(gallery.id, gallery);
    return gallery;
  }

  /**
   * Get gallery
   */
  getGallery(galleryId: string): Gallery | undefined {
    return this.galleries.get(galleryId);
  }

  /**
   * Get all galleries
   */
  getAllGalleries(): Gallery[] {
    return Array.from(this.galleries.values());
  }

  /**
   * Get styles in a gallery
   */
  getGalleryStyles(galleryId: string): Style[] {
    return this.getAllStyles(null, galleryId);
  }

  // ============================================================================
  // Image Library & Change Tracker Access
  // ============================================================================

  /**
   * Get image library instance
   */
  getImageLibrary(): ImageLibrary {
    return this.imageLibrary;
  }

  /**
   * Get change tracker instance
   */
  getChangeTracker(): ChangeTracker {
    return this.changeTracker;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Clone a style (for customization)
   */
  cloneStyle(styleId: string, newName: string | null = null): Style {
    const style = this.getResolvedStyle(styleId);
    if (!style) {
      throw new Error(`Style ${styleId} not found`);
    }
    
    return this.createStyle({
      name: newName || `${style.name} (Copy)`,
      type: style.type,
      variables: { ...style.variables },
      gallery: 'custom',
      metadata: style.metadata
    });
  }

  /**
   * Import style from JSON
   */
  importStyle(data: string | Partial<Style>): Style {
    const styleData = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (!styleData.name || !styleData.type || !styleData.variables) {
      throw new Error('Invalid style data: missing required fields');
    }
    
    return this.createStyle({
      name: styleData.name,
      type: styleData.type,
      variables: styleData.variables as StyleVariables,
      gallery: 'custom',
      metadata: styleData.metadata
    });
  }

  /**
   * Export style to JSON
   */
  exportStyle(styleId: string): string {
    const style = this.getResolvedStyle(styleId);
    if (!style) {
      throw new Error(`Style ${styleId} not found`);
    }
    
    return JSON.stringify(style, null, 2);
  }

  // ============================================================================
  // Event Listeners
  // ============================================================================

  /**
   * Subscribe to style changes
   */
  subscribe(listener: (event: string, ...args: any[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of an event
   */
  private notifyListeners(event: string, ...args: any[]): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, ...args);
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }

  // ============================================================================
  // Performance Monitoring
  // ============================================================================

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats() {
    return this.perfMonitor.getAllStats();
  }

  /**
   * Reset performance statistics
   */
  resetPerformanceStats(): void {
    this.cache.resetStats();
    this.perfMonitor.reset();
  }

  /**
   * Flush pending DOM updates immediately
   */
  flushDOMUpdates(): void {
    this.cssSetter.flush();
  }

  /**
   * Clear all caches
   */
  clearCaches(): void {
    this.cache.invalidateAll();
  }

  // ============================================================================
  // Inheritance & Variants
  // ============================================================================

  /**
   * Get inheritance manager
   */
  getInheritanceManager(): InheritanceManager {
    return this.inheritanceManager;
  }

  /**
   * Get variant manager
   */
  getVariantManager(): VariantManager {
    return this.variantManager;
  }

  /**
   * Get inheritance chain for a style
   */
  getInheritanceChain(styleId: string) {
    return this.inheritanceManager.getInheritanceChain(styleId);
  }

  /**
   * Analyze inheritance for a style
   */
  analyzeInheritance(styleId: string) {
    return this.inheritanceManager.analyzeInheritance(styleId);
  }

  /**
   * Create size variants (small, medium, large)
   */
  createSizeVariants(baseStyleId: string, config?: any) {
    const baseStyle = this.getStyle(baseStyleId);
    if (!baseStyle) {
      throw new Error(`Base style ${baseStyleId} not found`);
    }
    const variants = this.variantManager.createSizeVariants(baseStyle, config);
    
    // Add variants to styles map
    variants.forEach(variant => {
      this.styles.set(variant.id, variant);
      this.notifyListeners('styleCreated', variant);
    });
    
    return variants;
  }

  /**
   * Create color variants (primary, secondary, success, danger, warning)
   */
  createColorVariants(baseStyleId: string, config?: any) {
    const baseStyle = this.getStyle(baseStyleId);
    if (!baseStyle) {
      throw new Error(`Base style ${baseStyleId} not found`);
    }
    const variants = this.variantManager.createColorVariants(baseStyle, config);
    
    // Add variants to styles map
    variants.forEach(variant => {
      this.styles.set(variant.id, variant);
      this.notifyListeners('styleCreated', variant);
    });
    
    return variants;
  }

  /**
   * Create state variants (default, hover, active, disabled)
   */
  createStateVariants(baseStyleId: string, config?: any) {
    const baseStyle = this.getStyle(baseStyleId);
    if (!baseStyle) {
      throw new Error(`Base style ${baseStyleId} not found`);
    }
    const variants = this.variantManager.createStateVariants(baseStyle, config);
    
    // Add variants to styles map
    variants.forEach(variant => {
      this.styles.set(variant.id, variant);
      this.notifyListeners('styleCreated', variant);
    });
    
    return variants;
  }

  /**
   * Get all variants of a base style
   */
  getVariants(baseStyleId: string) {
    return this.variantManager.getVariants(baseStyleId, this.styles);
  }

  // ============================================================================
  // Storage
  // ============================================================================

  /**
   * Serialize to JSON
   */
  toJSON(): {
    styles: Style[];
    galleries: Gallery[];
    activeStyles: Array<[string, string]>;
    version: string;
  } {
    return {
      styles: Array.from(this.styles.values()).filter(s => s.custom),
      galleries: Array.from(this.galleries.values()),
      activeStyles: Array.from(this.activeStyles.entries()),
      version: '1.0.0'
    };
  }

  /**
   * Load from storage
   */
  private loadFromStorage(): void {
    // TODO: Implement Aggregatr integration for loading
    // For now, this is a placeholder
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let stylrInstance: StylrManager | null = null;

/**
 * Get or create the global Stylr instance
 */
export function getStylr(options?: StylrManagerOptions): StylrManager {
  if (!stylrInstance) {
    stylrInstance = new StylrManager(options);
  }
  return stylrInstance;
}

/**
 * Reset the global instance (useful for testing)
 */
export function resetStylr(): void {
  stylrInstance = null;
}
